import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    ViewChild,
    inject,
    resource,
} from '@angular/core';
import { RouterLink, NavigationEnd, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../../services/api.service';
import { LanguageService } from '../../../services/language.service';
import { SlugService } from '../../../services/slug.service';
import { ButtonComponent } from '../../common/button/button.component';
import { SocialMediaLinksComponent } from '../../common/social-media-links/social-media-links.component';
import { Navbar } from '../../../types/navbar.types';
import { CartComponent } from '../cart/cart.component';
import { CartService } from '../../../services/cart.service';
import gsap from 'gsap';
import { DOMHandlerService } from '../../../services/domhandler.service';
import { CartIconComponent } from './cart-icon/cart-icon.component';
import { UmamiService } from '../../../services/umami.service';
import { NavbarItemsComponent } from './navbar-items/navbar-items.component';

@Component({
    selector: 'app-navbar-hof',
    templateUrl: './navbar-hof.component.html',
    imports: [
        RouterLink,
        ButtonComponent,
        NgClass,
        RouterLink,
        SocialMediaLinksComponent,
        TranslatePipe,
        CartComponent,
        CartIconComponent,
        NavbarItemsComponent,
    ],
    host: {
        '[class.relative]': '!slugService.isHome()',
        '[class.block]': '!slugService.isHome()',
        '[class.h-24]': '!slugService.isHome()',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarHofComponent {
    languageService = inject(LanguageService);
    slugService = inject(SlugService);
    domHandler = inject(DOMHandlerService);
    cartService = inject(CartService);
    umami = inject(UmamiService);
    private router = inject(Router);
    private apiService: ApiService = inject(ApiService);
    private navbarElement: ElementRef | null = null;
    private scrollHandler?: () => void;

    navbarData = resource({
        request: this.languageService.languageCode,
        loader: () =>
            firstValueFrom<Navbar>(
                this.apiService.getHofNavbar(
                    this.languageService.languageCode(),
                ),
            ),
    });
    @ViewChild('navbar') set navbarItemsRef(value: ElementRef) {
        if (value) {
            this.navbarElement = value;
            this.scrollHandler = () => this.onScroll();
            window.addEventListener('scroll', this.scrollHandler);
        }
    }

    ngOnInit() {
        this.router.events.subscribe((event: any) => {
            if (event instanceof NavigationEnd) {
                this.cartService.closeCart();
            }
        });
    }

    private onScroll() {
        if (!this.navbarElement) {
            return;
        }
        const navbarElement = this.navbarElement.nativeElement;

        const scrollTop = window.scrollY;
        const navbarOffset = navbarElement.offsetTop;
        const animationDuration = 0.25;
        const logo = document.getElementById('logo');
        const chevronDowns = document.getElementsByClassName('chevron-down');
        const socialMediaLinks = document.getElementById('social-media-links');
        const slugStartWithHome = this.slugService.isHome();
        const white = '#F2F2F2';
        const black = 'hsl(345, 6%, 10%)';

        if (scrollTop > navbarOffset) {
            gsap.to(navbarElement, {
                backgroundColor: slugStartWithHome ? white : black,
                borderRadius: '9999px',
                width: '97.5vw',
                marginLeft: '1vw',
                boxShadow: slugStartWithHome
                    ? '0 0 10px rgba(0, 0, 0, 0.1)'
                    : '0 0 10px rgba(255, 255, 255, 0.1)',
                color: slugStartWithHome ? black : white,
                padding: '1rem',
                top: '1rem',
                duration: animationDuration,
                ease: 'linear',
            });
            if (logo) {
                gsap.to(logo, {
                    height: '3rem',
                    duration: animationDuration,
                    ease: 'linear',
                });
            }
            if (socialMediaLinks) {
                gsap.to(socialMediaLinks, {
                    fill: slugStartWithHome ? black : white,
                    duration: animationDuration,
                    ease: 'linear',
                });
            }
            if (chevronDowns && this.domHandler.isDesktop) {
                gsap.to(chevronDowns, {
                    stroke: slugStartWithHome ? black : white,
                    duration: animationDuration,
                    ease: 'linear',
                });
            }
        } else {
            gsap.to(navbarElement, {
                backgroundColor: 'transparent',
                borderRadius: '0',
                width: '100%',
                marginLeft: '0',
                boxShadow: 'none',
                color: slugStartWithHome ? white : black,
                padding: '1.5rem',
                top: '0',
                duration: animationDuration,
                ease: 'linear',
            });
            if (logo && this.domHandler.isDesktop) {
                gsap.to(logo, {
                    height: '6rem',
                    duration: animationDuration,
                    ease: 'linear',
                });
            }
            if (socialMediaLinks) {
                gsap.to(socialMediaLinks, {
                    fill: slugStartWithHome ? white : black,
                    duration: animationDuration,
                    ease: 'linear',
                });
            }
            if (chevronDowns && this.domHandler.isDesktop) {
                gsap.to(chevronDowns, {
                    stroke: slugStartWithHome ? white : black,
                    duration: animationDuration,
                    ease: 'linear',
                });
            }
        }
    }

    ngOnDestroy() {
        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler);
        }
    }
}
